import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/Layout/AdminLayout';
import { PageHeader } from '../../components/common';
import {
  Box,
  Button,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Switch,
  FormControlLabel,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Chip,
  Skeleton,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Add,
  Delete,
  AttachMoney,
  CreditCard,
  DirectionsCar,
  Restaurant,
  Business,
  Home,
  ShoppingCart,
  LocalShipping,
  Build,
  AccountBalance,
  Apartment,
  LocalHospital,
  School,
  Park,
  Agriculture,
  WaterDrop,
  LocalBar,
  Storefront,
  Engineering,
  ArrowBack,
} from '@mui/icons-material';
import { moduleService, UpdateModuleData } from '../../services/module.service';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import apiClient from '../../api/client';
import { colors } from '../../theme/theme';
import { useSnackbar } from '../../contexts/SnackbarContext';

export const EditModulePage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const [submitError, setSubmitError] = useState('');
  const [currentStep, setCurrentStep] = useState(0);
  const fieldsContainerRef = useRef<HTMLDivElement>(null);
  const lastFieldRef = useRef<HTMLInputElement>(null);

  // Icon options for modules
  const iconOptions = [
    { name: 'Money', icon: AttachMoney, value: 'AttachMoney' },
    { name: 'Card', icon: CreditCard, value: 'CreditCard' },
    { name: 'Car', icon: DirectionsCar, value: 'DirectionsCar' },
    { name: 'Restaurant', icon: Restaurant, value: 'Restaurant' },
    { name: 'Business', icon: Business, value: 'Business' },
    { name: 'House', icon: Home, value: 'Home' },
    { name: 'Shopping', icon: ShoppingCart, value: 'ShoppingCart' },
    { name: 'Shipping', icon: LocalShipping, value: 'LocalShipping' },
    { name: 'Tools', icon: Build, value: 'Build' },
    { name: 'Bank', icon: AccountBalance, value: 'AccountBalance' },
    { name: 'Building', icon: Apartment, value: 'Apartment' },
    { name: 'Hospital', icon: LocalHospital, value: 'LocalHospital' },
    { name: 'School', icon: School, value: 'School' },
    { name: 'Park', icon: Park, value: 'Park' },
    { name: 'Agriculture', icon: Agriculture, value: 'Agriculture' },
    { name: 'Water', icon: WaterDrop, value: 'WaterDrop' },
    { name: 'Bar', icon: LocalBar, value: 'LocalBar' },
    { name: 'Store', icon: Storefront, value: 'Storefront' },
    { name: 'Engineering', icon: Engineering, value: 'Engineering' },
  ];

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: '1',
    module_type_id: '',
    property_type: '',
    has_units: '0',
    units: '',
    icon: 'AttachMoney',
    background_color: colors.primary.main,
    touch_point_ids: [] as number[],
    fields: [] as Array<{
      field_name: string;
      field_type: string;
      field_options: string;
      field_required: boolean;
    }>,
    status: 'active',
  });

  // Fetch module data
  const { data: moduleData, isLoading: isLoadingModule } = useQuery({
    queryKey: ['module', id],
    queryFn: async () => {
      if (!id) return null;
      const response = await moduleService.getModule(parseInt(id));
      return response;
    },
    enabled: !!id,
  });

  // Fetch module types
  const { data: moduleTypesData } = useQuery({
    queryKey: ['moduleTypes'],
    queryFn: async () => {
      const response = await moduleService.getModuleTypes();
      const data = (response as any).data?.data || (response as any).data || response;
      return Array.isArray(data) ? data : [];
    },
  });

  const moduleTypes = moduleTypesData || [];

  // Fetch touch points
  const { data: touchPointsData } = useQuery({
    queryKey: ['touchPoints'],
    queryFn: async () => {
      const response = await apiClient.get('/admin/county/touch-points');
      const data = response.data?.data || response.data || [];
      return Array.isArray(data) ? data : [];
    },
  });

  const touchPoints = touchPointsData || [];

  // Property type options
  const propertyTypeOptions = [
    'Vehicle',
    'Land',
    'Plot',
    'Shop',
    'Stall',
    'Market',
    'Building',
    'Advertisement',
  ];

  // Reset current step when module ID changes
  useEffect(() => {
    setCurrentStep(0);
  }, [id]);

  // Load module data into form
  useEffect(() => {
    if (moduleData) {
      const module = (moduleData as any).data || moduleData;

      // Normalize fields data
      const normalizedFields = Array.isArray(module.fields)
        ? module.fields.map((field: any) => ({
            field_name: field.field_name || '',
            field_type: field.field_type || 'text',
            field_options: field.field_options || '',
            field_required: field.field_required === true || field.field_required === '1' || field.field_required === 1,
          }))
        : [];

      setFormData({
        name: module.name || '',
        description: module.description || '',
        type: module.type?.toString() || '1',
        module_type_id: module.module_type_id?.toString() || '',
        property_type: module.property_type || '',
        has_units: module.has_units ? '1' : '0',
        units: module.units || '',
        icon: module.icon || 'AttachMoney',
        background_color: module.background_color || colors.primary.main,
        touch_point_ids: Array.isArray(module.touch_point_ids) ? module.touch_point_ids : [],
        fields: normalizedFields,
        status: module.status || 'active',
      });
    }
  }, [moduleData]);

  // Default fields for different module types
  const getDefaultFieldsForModuleType = (moduleTypeId: string) => {
    const moduleType = moduleTypes.find((mt: any) => mt.id === parseInt(moduleTypeId));
    const typeName = moduleType?.name?.toLowerCase() || '';

    if (typeName.includes('business')) {
      return [
        { field_name: 'Business Name', field_type: 'text', field_options: '', field_required: true },
        { field_name: 'Business Type', field_type: 'select', field_options: 'Sole Proprietorship,Partnership,Limited Company,NGO', field_required: true },
        { field_name: 'Registration Number', field_type: 'text', field_options: '', field_required: true },
        { field_name: 'Business Address', field_type: 'textarea', field_options: '', field_required: true },
        { field_name: 'Contact Person', field_type: 'text', field_options: '', field_required: true },
        { field_name: 'Phone Number', field_type: 'text', field_options: '', field_required: true },
        { field_name: 'Email Address', field_type: 'email', field_options: '', field_required: false },
      ];
    } else if (typeName.includes('property')) {
      return [
        { field_name: 'Property Type', field_type: 'text', field_options: '', field_required: true },
        { field_name: 'Plot Number', field_type: 'text', field_options: '', field_required: true },
        { field_name: 'Property Address', field_type: 'textarea', field_options: '', field_required: true },
        { field_name: 'Owner Name', field_type: 'text', field_options: '', field_required: true },
        { field_name: 'Owner Contact', field_type: 'text', field_options: '', field_required: true },
      ];
    } else if (typeName.includes('vehicle') || typeName.includes('parking')) {
      return [
        { field_name: 'Vehicle Registration Number', field_type: 'text', field_options: '', field_required: true },
        { field_name: 'Vehicle Type', field_type: 'select', field_options: 'Car,Motorcycle,Truck,Bus,Van', field_required: true },
        { field_name: 'Vehicle Make', field_type: 'text', field_options: '', field_required: true },
        { field_name: 'Vehicle Model', field_type: 'text', field_options: '', field_required: true },
        { field_name: 'Owner Name', field_type: 'text', field_options: '', field_required: true },
        { field_name: 'Owner Contact', field_type: 'text', field_options: '', field_required: true },
      ];
    }

    return [];
  };

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: UpdateModuleData) => {
      if (!id) throw new Error('Module ID is required');
      return moduleService.updateModule(parseInt(id), data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modules'] });
      queryClient.invalidateQueries({ queryKey: ['module', id] });
      setSubmitError('');
      showSnackbar('Module updated successfully!', 'success');
      navigate('/modules');
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Failed to update module';
      setSubmitError(errorMessage);
      showSnackbar(errorMessage, 'error');
    },
  });

  const handleNext = () => {
    setCurrentStep((prev) => {
      const nextStep = prev + 1;
      return nextStep;
    });
  };

  const handleBack = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handleCancel = () => {
    navigate('/modules');
  };

  const handleAddField = () => {
    const newField = {
      field_name: '',
      field_type: 'text',
      field_options: '',
      field_required: false,
    };

    // Add at the end
    setFormData({
      ...formData,
      fields: [...formData.fields, newField],
    });

    // Focus the new field after it's rendered
    setTimeout(() => {
      lastFieldRef.current?.focus();
      lastFieldRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const handleSubmit = () => {
    if (!formData.name) {
      setSubmitError('Please fill in all required fields');
      return;
    }

    // Validate based on structure type
    if (formData.type === '2' && !formData.module_type_id) {
      setSubmitError('Module Type is required for Structured modules');
      return;
    }

    const data: any = {
      name: formData.name,
      description: formData.description,
      type: parseInt(formData.type),
      icon: formData.icon,
      background_color: formData.background_color,
      is_billable: 1,
      status: formData.status,
    };

    // Add module_type_id only for structured modules
    if (formData.type === '2' && formData.module_type_id) {
      data.module_type_id = parseInt(formData.module_type_id);
    }

    // Add property_type if selected
    if (formData.property_type) {
      data.property_type = formData.property_type;
    }

    // Add has_units and units
    if (formData.has_units === '1' && formData.units) {
      data.has_units = true;
      data.units = formData.units;
    } else {
      data.has_units = false;
    }

    // Add touch_point_ids
    if (formData.touch_point_ids.length > 0) {
      data.touch_point_ids = formData.touch_point_ids;
    }

    // Add fields for both unstructured and structured modules
    if (formData.fields.length > 0) {
      data.fields = formData.fields;
    }


    updateMutation.mutate(data);
  };

  const breadcrumbs = [
    { label: 'Modules', path: '/modules' },
    { label: `Edit Module: ${formData.name}` },
  ];

  if (isLoadingModule) {
    return (
      <AdminLayout>
        <Box>
          <Skeleton variant="rectangular" height={120} sx={{ mb: 3, borderRadius: 2 }} />
          <Skeleton variant="rectangular" height={600} sx={{ borderRadius: 4 }} />
        </Box>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title={`Edit Module: ${formData.name}`}
          subtitle="Update module configuration and settings"
          breadcrumbs={breadcrumbs}
          action={
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={handleCancel}
              sx={{
                borderColor: '#cbd5e1',
                color: '#64748b',
                '&:hover': {
                  borderColor: '#94a3b8',
                  bgcolor: '#f8fafc',
                },
              }}
            >
              Back to Modules
            </Button>
          }
        />

        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: 4,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.06)',
            border: '1px solid #e2e8f0',
            overflow: 'hidden',
          }}
        >
          {/* Stepper Header */}
          <Box
            sx={{
              px: { xs: 3, sm: 4, md: 6 },
              py: 4,
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              borderBottom: '2px solid #e2e8f0',
            }}
          >
            <Stepper
              activeStep={currentStep}
              alternativeLabel
              sx={{
                '& .MuiStepLabel-label': {
                  color: '#64748b',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  '&.Mui-active': {
                    color: colors.primary.main,
                    fontWeight: 700,
                  },
                  '&.Mui-completed': {
                    color: '#059669',
                    fontWeight: 600,
                  },
                },
                '& .MuiStepIcon-root': {
                  color: '#cbd5e1',
                  fontSize: '2rem',
                  transition: 'all 0.3s ease',
                  '&.Mui-active': {
                    color: colors.primary.main,
                    transform: 'scale(1.1)',
                  },
                  '&.Mui-completed': {
                    color: '#059669',
                  },
                },
                '& .MuiStepConnector-line': {
                  borderColor: '#e2e8f0',
                  borderTopWidth: 2,
                },
              }}
            >
              <Step>
                <StepLabel>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ fontWeight: 'inherit' }}>Step 1</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'inherit' }}>Basic Info</Typography>
                  </Box>
                </StepLabel>
              </Step>
              <Step>
                <StepLabel>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ fontWeight: 'inherit' }}>Step 2</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'inherit' }}>Configuration</Typography>
                  </Box>
                </StepLabel>
              </Step>
              <Step>
                <StepLabel>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ fontWeight: 'inherit' }}>Step 3</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'inherit' }}>Customization</Typography>
                  </Box>
                </StepLabel>
              </Step>
            </Stepper>
          </Box>

          {/* Content */}
          <Box
            sx={{
              px: 5,
              py: 5,
              maxHeight: 'calc(100vh - 450px)',
              minHeight: '400px',
              overflowY: 'auto',
              bgcolor: '#fafbff',
            }}
          >
            {submitError && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {submitError}
              </Alert>
            )}

            {/* Step 1: Basic Info */}
            {currentStep === 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 800, mx: 'auto' }}>
                {/* Module Name */}
                <TextField
                  fullWidth
                  required
                  label="Module Name"
                  placeholder="e.g., Trade License, Land Rates, Parking Fees"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 3,
                      bgcolor: 'white',
                      '&:hover': {
                        boxShadow: `0 4px 12px ${colors.primary.main}10`,
                      },
                      '&.Mui-focused': {
                        boxShadow: `0 4px 12px ${colors.primary.main}20`,
                      },
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: colors.primary.main,
                    },
                  }}
                />

                {/* Description */}
                <TextField
                  fullWidth
                  label="Description"
                  placeholder="Describe the module purpose..."
                  multiline
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    },
                  }}
                />

                {/* Structure */}
                <FormControl fullWidth required>
                  <InputLabel>Structure</InputLabel>
                  <Select
                    value={formData.type}
                    label="Structure"
                    onChange={(e) => setFormData({
                      ...formData,
                      type: e.target.value,
                      module_type_id: e.target.value === '1' ? '' : formData.module_type_id
                    })}
                    sx={{
                      borderRadius: 2,
                    }}
                  >
                    <MenuItem value="1">Unstructured</MenuItem>
                    <MenuItem value="2">Structured</MenuItem>
                  </Select>
                </FormControl>

                {/* Module Type - Only for Structured */}
                {formData.type === '2' && (
                  <FormControl fullWidth>
                    <InputLabel>Module Type (Optional)</InputLabel>
                    <Select
                      value={formData.module_type_id}
                      label="Module Type (Optional)"
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          module_type_id: e.target.value,
                        });
                      }}
                      sx={{
                        borderRadius: 2,
                      }}
                    >
                      <MenuItem value="">
                        <em>None - General Module</em>
                      </MenuItem>
                      {moduleTypes.map((type: any) => (
                        <MenuItem key={type.id} value={type.id}>
                          {type.name}
                        </MenuItem>
                      ))}
                    </Select>
                    <Typography variant="caption" sx={{ mt: 0.5, color: '#64748b', ml: 1.5 }}>
                      Select a specific type or leave as General for other modules
                    </Typography>
                  </FormControl>
                )}

                {/* Property Type - Conditional */}
                {formData.type === '2' && formData.module_type_id && (() => {
                  const selectedModuleType = moduleTypes.find((mt: any) => mt.id === parseInt(formData.module_type_id));
                  const isPropertyModule = selectedModuleType?.name?.toLowerCase().includes('property');
                  return isPropertyModule;
                })() && (
                  <FormControl fullWidth>
                    <InputLabel>Property Type</InputLabel>
                    <Select
                      value={formData.property_type}
                      label="Property Type"
                      onChange={(e) => setFormData({ ...formData, property_type: e.target.value })}
                      sx={{
                        borderRadius: 2,
                      }}
                    >
                      <MenuItem value="">
                        <em>Select property type...</em>
                      </MenuItem>
                      {propertyTypeOptions.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {/* Has Units */}
                <FormControl fullWidth>
                  <InputLabel>Has Units?</InputLabel>
                  <Select
                    value={formData.has_units}
                    label="Has Units?"
                    onChange={(e) => setFormData({
                      ...formData,
                      has_units: e.target.value,
                      units: e.target.value === '0' ? '' : formData.units
                    })}
                    sx={{
                      borderRadius: 2,
                    }}
                  >
                    <MenuItem value="0">No</MenuItem>
                    <MenuItem value="1">Yes</MenuItem>
                  </Select>
                </FormControl>

                {/* Unit Name */}
                {formData.has_units === '1' && (
                  <TextField
                    fullWidth
                    required
                    label="Unit Name"
                    placeholder="e.g., Trucks, Floors, Seats"
                    value={formData.units}
                    onChange={(e) => setFormData({ ...formData, units: e.target.value })}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                )}
              </Box>
            )}

            {/* Step 2: Configuration */}
            {currentStep === 1 && (() => {
              return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 900, mx: 'auto' }}>
                {/* Show default fields as informational */}
                {formData.type === '2' && formData.module_type_id && (() => {
                  const defaultFields = getDefaultFieldsForModuleType(formData.module_type_id);
                  return defaultFields.length > 0 ? (
                    <Box>
                      <Alert severity="success" icon={false} sx={{ borderRadius: 2, mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          Pre-defined Fields for {moduleTypes.find((mt: any) => mt.id === parseInt(formData.module_type_id))?.name}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1.5, color: '#64748b' }}>
                          The following fields are automatically captured for this module type. No need to add them again:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {defaultFields.map((field, index) => (
                            <Chip
                              key={index}
                              label={field.field_name}
                              size="small"
                              sx={{
                                bgcolor: '#ecfdf5',
                                color: '#065f46',
                                fontWeight: 600,
                                '& .MuiChip-label': {
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                },
                              }}
                              icon={field.field_required ? <Box component="span" sx={{ fontSize: '0.75rem', color: '#dc2626' }}>*</Box> : undefined}
                            />
                          ))}
                        </Box>
                      </Alert>
                    </Box>
                  ) : null;
                })()}

                {/* Custom Fields */}
                <Box>
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#334155' }}>
                      Module Fields
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      {formData.type === '1'
                        ? 'Define data fields for this unstructured module'
                        : 'Optionally add informational fields (structured modules use Schedules/Parts/Services)'}
                    </Typography>
                  </Box>
                  <Box
                    ref={fieldsContainerRef}
                    sx={{
                      border: '1px solid #e2e8f0',
                      borderRadius: 2,
                      bgcolor: '#f9fafb',
                      maxHeight: 500,
                      overflowY: 'auto',
                    }}
                  >
                    {formData.fields.length === 0 ? (
                      <Box sx={{ p: 6, textAlign: 'center' }}>
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            bgcolor: colors.primary[50],
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 2,
                          }}
                        >
                          <Add sx={{ fontSize: 40, color: colors.primary.main }} />
                        </Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                          No Fields Defined Yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                          {formData.type === '1'
                            ? 'Add custom fields to capture data for this unstructured module.'
                            : formData.module_type_id
                              ? 'Load template fields or add custom informational fields (optional for structured modules).'
                              : 'Select a module type first, then you can load template fields or add custom fields.'}
                        </Typography>
                        <Button
                          variant="outlined"
                          startIcon={<Add />}
                          onClick={() => handleAddField()}
                          sx={{
                            borderColor: colors.primary.main,
                            color: colors.primary.main,
                            '&:hover': {
                              borderColor: colors.primary.dark,
                              bgcolor: colors.primary[50],
                            },
                          }}
                        >
                          Add Your First Field
                        </Button>
                      </Box>
                    ) : (
                      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {formData.fields.map((field, index) => (
                          <Box
                            key={index}
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              border: '1px solid #e2e8f0',
                              bgcolor: 'white',
                            }}
                          >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: colors.primary.main }}>
                                  Field {index + 1}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setFormData({
                                      ...formData,
                                      fields: formData.fields.filter((_, i) => i !== index),
                                    });
                                  }}
                                  sx={{ color: '#dc2626' }}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Box>
                            <Grid container spacing={2}>
                              <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                  fullWidth
                                  required
                                  size="small"
                                  label="Field Name"
                                  placeholder="e.g., Business Name"
                                  value={field.field_name}
                                  onChange={(e) => {
                                    const newFields = [...formData.fields];
                                    newFields[index].field_name = e.target.value;
                                    setFormData({ ...formData, fields: newFields });
                                  }}
                                  inputRef={index === formData.fields.length - 1 ? lastFieldRef : undefined}
                                />
                              </Grid>
                              <Grid size={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth size="small" required>
                                  <InputLabel>Field Type</InputLabel>
                                  <Select
                                    value={field.field_type}
                                    label="Field Type"
                                    onChange={(e) => {
                                      const newFields = [...formData.fields];
                                      newFields[index].field_type = e.target.value;
                                      setFormData({ ...formData, fields: newFields });
                                    }}
                                  >
                                    <MenuItem value="text">Text</MenuItem>
                                    <MenuItem value="email">Email</MenuItem>
                                    <MenuItem value="number">Number</MenuItem>
                                    <MenuItem value="date">Date</MenuItem>
                                    <MenuItem value="textarea">Text Area</MenuItem>
                                    <MenuItem value="select">Dropdown</MenuItem>
                                    <MenuItem value="radio">Radio Buttons</MenuItem>
                                    <MenuItem value="checkbox">Checkboxes</MenuItem>
                                    <MenuItem value="file">File Upload</MenuItem>
                                  </Select>
                                </FormControl>
                              </Grid>
                              {['select', 'radio', 'checkbox'].includes(field.field_type) && (
                                <Grid size={{ xs: 12 }}>
                                  <TextField
                                    fullWidth
                                    required
                                    size="small"
                                    label="Options (comma-separated)"
                                    placeholder="e.g., Option 1, Option 2, Option 3"
                                    value={field.field_options}
                                    onChange={(e) => {
                                      const newFields = [...formData.fields];
                                      newFields[index].field_options = e.target.value;
                                      setFormData({ ...formData, fields: newFields });
                                    }}
                                  />
                                </Grid>
                              )}
                              <Grid size={{ xs: 12 }}>
                                <FormControlLabel
                                  control={
                                    <Switch
                                      checked={field.field_required}
                                      onChange={(e) => {
                                        const newFields = [...formData.fields];
                                        newFields[index].field_required = e.target.checked;
                                        setFormData({ ...formData, fields: newFields });
                                      }}
                                      sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                          color: colors.primary.main,
                                        },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                          backgroundColor: colors.primary.main,
                                        },
                                      }}
                                    />
                                  }
                                  label={
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      Required Field
                                    </Typography>
                                  }
                                />
                              </Grid>
                            </Grid>
                          </Box>
                        ))}

                        {/* Add Field Button at the end */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
                          <Button
                            variant="outlined"
                            startIcon={<Add />}
                            onClick={() => handleAddField()}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 600,
                              color: colors.primary.main,
                              borderColor: colors.primary.main,
                              borderRadius: 2,
                              px: 3,
                              py: 1,
                              '&:hover': {
                                bgcolor: colors.primary[50],
                                borderColor: colors.primary.dark,
                              },
                            }}
                          >
                            Add Field
                          </Button>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Touch Points */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#334155' }}>
                    Touch Points
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: 1.5,
                      p: 2,
                      borderRadius: 2,
                      border: '1px solid #e2e8f0',
                      bgcolor: '#f9fafb',
                    }}
                  >
                    {(() => {
                      const standardChannels = [
                        { key: 'customer_portal', label: 'Customer Portal', description: 'Web-based self-service portal' },
                        { key: 'mobile_app', label: 'Mobile App', description: 'Android/iOS mobile application' },
                        { key: 'ussd', label: 'USSD', description: 'USSD-based access for feature phones' },
                      ];

                      return standardChannels.map((channel) => {
                        const countyTp = touchPoints.find((tp: any) => tp.touch_point?.toLowerCase() === channel.key);
                        const isEnabled = countyTp && countyTp.status === 'active';
                        const isSelected = countyTp ? formData.touch_point_ids.includes(countyTp.id) : false;

                        return (
                          <Box key={channel.key} sx={{ p: 1.5, borderRadius: 1.5, border: `1px solid ${isEnabled ? '#e2e8f0' : '#f1f5f9'}`, bgcolor: isEnabled ? 'white' : '#f8fafc', opacity: isEnabled ? 1 : 0.7 }}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={isSelected}
                                  disabled={!isEnabled}
                                  onChange={(e) => {
                                    if (!countyTp) return;
                                    if (e.target.checked) {
                                      setFormData({ ...formData, touch_point_ids: [...formData.touch_point_ids, countyTp.id] });
                                    } else {
                                      setFormData({ ...formData, touch_point_ids: formData.touch_point_ids.filter((id: number) => id !== countyTp.id) });
                                    }
                                  }}
                                  sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: colors.primary.main }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: colors.primary.main } }}
                                />
                              }
                              label={
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: isEnabled ? '#334155' : '#94a3b8' }}>
                                    {channel.label}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                    {channel.description}
                                  </Typography>
                                </Box>
                              }
                              sx={{ m: 0, width: '100%' }}
                            />
                            {!isEnabled && (
                              <Typography variant="caption" sx={{ color: '#ef4444', display: 'block', mt: 0.5, ml: 5.5 }}>
                                Not enabled. Contact your administrator to activate.
                              </Typography>
                            )}
                          </Box>
                        );
                      });
                    })()}
                    {touchPoints.filter((tp: any) => !['customer_portal', 'mobile_app', 'ussd'].includes(tp.touch_point?.toLowerCase())).map((tp: any) => {
                      const displayName = tp.touch_point.split('_').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                      const isSelected = formData.touch_point_ids.includes(tp.id);
                      const isEnabled = tp.status === 'active';
                      return (
                        <Box key={tp.id} sx={{ p: 1.5, borderRadius: 1.5, border: `1px solid ${isEnabled ? '#e2e8f0' : '#f1f5f9'}`, bgcolor: isEnabled ? 'white' : '#f8fafc', opacity: isEnabled ? 1 : 0.7 }}>
                          <FormControlLabel
                            control={<Switch checked={isSelected} disabled={!isEnabled} onChange={(e) => {
                              if (e.target.checked) setFormData({ ...formData, touch_point_ids: [...formData.touch_point_ids, tp.id] });
                              else setFormData({ ...formData, touch_point_ids: formData.touch_point_ids.filter((id: number) => id !== tp.id) });
                            }} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: colors.primary.main }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: colors.primary.main } }} />}
                            label={<Typography variant="body2" sx={{ fontWeight: 600, color: isEnabled ? '#334155' : '#94a3b8' }}>{displayName}</Typography>}
                            sx={{ m: 0, width: '100%' }}
                          />
                          {!isEnabled && <Typography variant="caption" sx={{ color: '#ef4444', display: 'block', mt: 0.5, ml: 5.5 }}>Not enabled. Contact your administrator to activate.</Typography>}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
              );
            })()}

            {/* Step 3: Customization */}
            {currentStep === 2 && (() => {
              return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 800, mx: 'auto' }}>
                {/* Icon Selection */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#334155' }}>
                    Module Icon *
                  </Typography>
                  <Grid container spacing={1.5}>
                    {iconOptions.map((option) => {
                      const IconComponent = option.icon;
                      return (
                        <Grid key={option.value} size={{ xs: 3, sm: 2.4 }}>
                          <Box
                            onClick={() => setFormData({ ...formData, icon: option.value })}
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              border: '2px solid',
                              borderColor: formData.icon === option.value ? colors.primary.main : '#e2e8f0',
                              bgcolor: formData.icon === option.value ? colors.primary[50] : 'white',
                              cursor: 'pointer',
                              transition: 'all 0.2s',
                              textAlign: 'center',
                              '&:hover': {
                                borderColor: colors.primary.main,
                                bgcolor: colors.primary[50],
                                transform: 'translateY(-2px)',
                              },
                            }}
                          >
                            <IconComponent
                              sx={{
                                fontSize: 28,
                                color: formData.icon === option.value ? colors.primary.main : '#64748b',
                                mb: 0.5,
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                fontSize: '0.65rem',
                                color: formData.icon === option.value ? colors.primary.main : '#64748b',
                                fontWeight: formData.icon === option.value ? 600 : 500,
                              }}
                            >
                              {option.name}
                            </Typography>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>

                {/* Background Color */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#334155' }}>
                    Background Color *
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        p: 1.5,
                        borderRadius: 2,
                        border: '1px solid #e2e8f0',
                        bgcolor: 'white',
                      }}
                    >
                      <input
                        type="color"
                        value={formData.background_color}
                        onChange={(e) => setFormData({ ...formData, background_color: e.target.value })}
                        style={{
                          width: 48,
                          height: 48,
                          border: 'none',
                          borderRadius: 8,
                          cursor: 'pointer',
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" sx={{ display: 'block', color: '#64748b', fontSize: '0.7rem', mb: 0.5 }}>
                          Selected Color
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#334155' }}>
                          {formData.background_color}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: 2,
                        bgcolor: formData.background_color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      }}
                    >
                      {(() => {
                        const selectedIcon = iconOptions.find(opt => opt.value === formData.icon);
                        const IconComp = selectedIcon ? selectedIcon.icon : AttachMoney;
                        return <IconComp sx={{ fontSize: 32, color: 'white' }} />;
                      })()}
                    </Box>
                  </Box>
                </Box>

                {/* Status */}
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    label="Status"
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    sx={{
                      borderRadius: 2,
                    }}
                  >
                    <MenuItem value="active">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#16a34a' }} />
                        Active
                      </Box>
                    </MenuItem>
                    <MenuItem value="inactive">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#6b7280' }} />
                        Inactive
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
              );
            })()}
          </Box>

          {/* Actions */}
          <Box sx={{ px: 5, py: 3, gap: 2, borderTop: '2px solid #f1f5f9', bgcolor: '#fafafb', display: 'flex', alignItems: 'center' }}>
            <Button
              onClick={handleCancel}
              disabled={updateMutation.isPending}
              variant="outlined"
              sx={{
                px: 4,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 3,
                borderColor: '#cbd5e1',
                color: '#64748b',
                '&:hover': {
                  borderColor: '#94a3b8',
                  bgcolor: '#f8fafc',
                },
              }}
            >
              Cancel
            </Button>
            <Box sx={{ flex: 1 }} />
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {currentStep > 0 && (
                <Button
                  onClick={handleBack}
                  disabled={updateMutation.isPending}
                  variant="outlined"
                  sx={{
                    px: 4,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 3,
                    borderColor: colors.primary.main,
                    color: colors.primary.main,
                    '&:hover': {
                      borderColor: colors.primary.dark,
                      bgcolor: colors.primary[50],
                    },
                  }}
                >
                  Back
                </Button>
              )}
              {currentStep < 2 ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  sx={{
                    px: 5,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 3,
                    background: colors.gradients.primary,
                    boxShadow: colors.shadows.button,
                    '&:hover': {
                      background: colors.gradients.primaryHover,
                      boxShadow: colors.shadows.buttonHover,
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  Next Step
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={updateMutation.isPending}
                  startIcon={updateMutation.isPending ? <CircularProgress size={18} sx={{ color: 'white' }} /> : null}
                  sx={{
                    px: 5,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 3,
                    fontSize: '1rem',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                      boxShadow: '0 6px 16px rgba(16, 185, 129, 0.4)',
                      transform: 'translateY(-1px)',
                    },
                    '&:disabled': {
                      background: '#d1d5db',
                      boxShadow: 'none',
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  {updateMutation.isPending ? 'Updating Module...' : 'Update Module'}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </AdminLayout>
  );
};
